import React from "react";
import { CircleCheck, MessageCircle, ArrowRight, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
export default function StartRecoveryJourney() {
  return <section id="inicie-sua-jornada-de-recuperação" className="dark bg-background text-foreground relative min-h-screen flex items-center justify-center py-20 px-4 overflow-hidden">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-black/60 z-10" />
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559261/img/clinical_serene_bg.png)] bg-cover bg-center opacity-90" />
      </div>

      <div className="relative z-20 w-full max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
        
        {}
        <div className="text-center lg:text-left space-y-8">
          <Badge variant="secondary" className="px-4 py-1.5 text-sm backdrop-blur-md bg-secondary/80 text-secondary-foreground border-white/10">
            <CircleCheck className="w-4 h-4 mr-2" />
            Especialista Certificada em Tricologia
          </Badge>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold tracking-tight text-white drop-shadow-sm">
            Descubra o potencial do seu cabelo
          </h2>
          
          <p className="text-lg text-gray-200 max-w-xl mx-auto lg:mx-0 leading-relaxed">
            Combine ciência e cuidado humanizado para restaurar a saúde dos seus fios. 
            Nossa abordagem clínica detalhada identifica a raiz do problema para resultados duradouros.
          </p>

          <div className="hidden lg:block">
            <div className="flex flex-col gap-4 p-6 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <CircleCheck className="text-primary w-5 h-5" />
                Por que agendar agora?
              </h3>
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                  Diagnóstico tricoscópico de alta precisão
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                  Protocolos personalizados para sua necessidade
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                  Acompanhamento contínuo e humanizado
                </li>
              </ul>
            </div>
          </div>
        </div>

        {}
        <div className="w-full">
          <Card className="bg-card/10 backdrop-blur-xl border-white/10 shadow-2xl text-white overflow-hidden">
            <CardHeader className="pb-4 border-b border-white/10 bg-white/5">
              <CardTitle className="text-2xl font-serif text-white">Inicie sua Jornada</CardTitle>
              <CardDescription className="text-gray-300">
                Preencha o formulário para agendar sua avaliação.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <FormProvider formId="recovery_journey_form_home" sectionName="hero_cta_section" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              concern: "",
              message: ""
            }} validationRules={{
              full_name: {
                required: true,
                minLength: 3
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                minLength: 10
              },
              concern: {
                required: true
              },
              message: {
                required: false
              }
            }} submitText="Agendar Consulta" successMessage="Sua solicitação foi enviada com sucesso! Entraremos em contato em breve." errorMessage="Ocorreu um erro ao enviar. Por favor, tente novamente.">
                <RecoveryForm />
              </FormProvider>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}
function RecoveryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-10 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-2">
          <CircleCheck className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-xl font-semibold text-white">Solicitação Recebida!</h3>
        <p className="text-gray-300 max-w-xs mx-auto">{successMessage}</p>
        <Button variant="outline" className="mt-4 border-white/20 text-white hover:bg-white/10" onClick={() => window.location.reload()}>
          Enviar nova mensagem
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="full_name" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-gray-200">Nome Completo</FormLabel>
            <FormControl>
              <input {...field} placeholder="Maria Silva" className="flex h-10 w-full rounded-md border border-white/20 bg-black/20 px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all" />
            </FormControl>
            <FormMessage className="text-red-300" />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-gray-200">E-mail</FormLabel>
              <FormControl>
                <input {...field} type="email" placeholder="maria@email.com" className="flex h-10 w-full rounded-md border border-white/20 bg-black/20 px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all" />
              </FormControl>
              <FormMessage className="text-red-300" />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-gray-200">Telefone</FormLabel>
              <FormControl>
                <input {...field} type="tel" placeholder="(11) 98765-4321" className="flex h-10 w-full rounded-md border border-white/20 bg-black/20 px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all" />
              </FormControl>
              <FormMessage className="text-red-300" />
            </FormItem>} />
      </div>

      <FormField control={control} name="concern" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-gray-200">Principal problema capilar</FormLabel>
            <FormControl>
              <select {...field} className="flex h-10 w-full rounded-md border border-white/20 bg-black/20 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all appearance-none" style={{
          backgroundImage: 'none'
        }}>
                <option value="" disabled className="bg-card text-foreground">Selecione uma opção</option>
                <option value="Oleosidade" className="bg-card text-foreground">Oleosidade</option>
                <option value="Caspa" className="bg-card text-foreground">Caspa</option>
                <option value="Psoríase" className="bg-card text-foreground">Psoríase</option>
                <option value="Queda de cabelo" className="bg-card text-foreground">Queda de cabelo</option>
                <option value="Alopecia" className="bg-card text-foreground">Alopecia</option>
                <option value="Danos químicos" className="bg-card text-foreground">Danos químicos</option>
                <option value="Manutenção preventiva" className="bg-card text-foreground">Manutenção preventiva</option>
                <option value="Outro" className="bg-card text-foreground">Outro</option>
              </select>
            </FormControl>
            <FormMessage className="text-red-300" />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-gray-200">Mensagem (opcional)</FormLabel>
            <FormControl>
              <textarea {...field} placeholder="Descreva brevemente seu problema ou objetivo..." className="flex min-h-[80px] w-full rounded-md border border-white/20 bg-black/20 px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none" />
            </FormControl>
            <FormMessage className="text-red-300" />
          </FormItem>} />

      {submitError && <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-md">
          <p className="text-red-200 text-sm">{submitError}</p>
        </div>}

      <div className="pt-2 flex flex-col gap-3">
        <Button type="submit" disabled={isSubmitting} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-12 text-base shadow-lg shadow-primary/20">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 h-5 w-5" />
              Enviando...
            </> : <>
              Agendar Consulta
              <ArrowRight className="ml-2 h-5 w-5" />
            </>}
        </Button>

        <div className="relative flex items-center py-2">
          <div className="grow border-t border-white/10"></div>
          <span className="shrink-0 px-2 text-xs text-gray-400 uppercase">Ou</span>
          <div className="grow border-t border-white/10"></div>
        </div>

        <Button variant="outline" type="button" asChild className="w-full border-white/20 bg-transparent text-white hover:bg-white/10 h-11">
          <Link to="https://wa.me/5511912345678" newTab>
            <MessageCircle className="mr-2 h-4 w-4" />
            Falar com Assistente
          </Link>
        </Button>
      </div>
    </Form>;
}